import agent from '../../services/api/agent';
import coursePageAgent from '../../services/api/coursePageAgent';
import type { TCourseViewModel, TPageSnapshot, TTreeViewModel } from '../../services/models/CourseViewModels';
import { isAuthorsOwnImage } from '../../utils/pages/pageAvatar';

/**
 * The course page tree as the page list and the AI wizard read it: the legacy API's `Course/GetPages`,
 * with each page's page snapshot from the creator API as its page avatar wherever the author has not
 * chosen an image of their own.
 */

export const withPageSnapshots = (course: TCourseViewModel, snapshots: TPageSnapshot[]): TCourseViewModel => {
  const webPaths = new Map(snapshots.map((snapshot) => [String(snapshot.coursePageID), snapshot.webPath]));

  const withSnapshot = (page: TTreeViewModel): TTreeViewModel => {
    const webPath = webPaths.get(page.id);
    const children = page.children?.map(withSnapshot) ?? page.children;
    if (webPath === undefined || isAuthorsOwnImage(page.data?.avatar?.value)) return { ...page, children };

    return { ...page, children, data: { ...page.data, avatar: { ...page.data.avatar, value: webPath } } };
  };

  return { ...course, pages: course.pages?.map(withSnapshot) ?? course.pages };
};

/**
 * Fails only when the page tree does. Without the page snapshots the pages show what the legacy API
 * gave them, which is the default avatar for most.
 */
export const loadCoursePages = async (courseId: number): Promise<TCourseViewModel> => {
  const [course, snapshots] = await Promise.all([
    agent.Course.getPages(courseId),
    coursePageAgent.CoursePage.getPageSnapshots(courseId).catch((error: unknown): TPageSnapshot[] => {
      console.warn('The page snapshots could not be read; the page list shows the page avatars the legacy API gave', error);
      return [];
    }),
  ]);
  return withPageSnapshots(course, snapshots);
};
